import {isValid, parseISO} from 'date-fns'
import {buildRemoteSensingCacheKey} from './remoteSensingCacheKey'
import {IMAGE_TYPES_WITH_DATE, isHdImageType} from './imageTypeUtils'
import {REMOTE_SENSING_PRODUCTS, normalizeRemoteSensingSource} from './remoteSensingModel'
import {createDocumentScopedValue, readDocumentScopedValue} from '../landViewDocumentState'

const VIEW_IMAGE_STORAGE_PREFIX = 'specific:view-image:'

const normalizeSelectedDate = (value) => {
  if (value == null) {
    return null
  }

  const date =
    value instanceof Date
      ? value
      : typeof value === 'string'
      ? parseISO(value)
      : typeof value === 'number'
      ? new Date(value)
      : null

  return date && isValid(date) ? date : null
}

const SELECTION_FIELDS = [
  'selectedDate',
  'selectedAcquisitionId',
  'selectedAcquisitionStatus',
  'selectedAccessId',
  'selectedCompanyPeriodId',
  'selectedItemId',
  'selectedQualityWarning',
  'remoteSensingSource',
]

const getRemoteSensingProduct = (imageType) =>
  IMAGE_TYPES_WITH_DATE.has(imageType)
    ? normalizeRemoteSensingSource(imageType).product
    : null

const snapshotSelection = (land = {}) => {
  const selection = {selectedDate: normalizeSelectedDate(land.selectedDate)}
  SELECTION_FIELDS.slice(1).forEach((field) => {
    if (land[field] != null) {
      selection[field] = land[field]
    }
  })
  return selection
}

const applySelection = (land, selection = {}) => {
  SELECTION_FIELDS.forEach((field) => delete land[field])
  Object.assign(land, snapshotSelection(selection))
  return land
}

const storeProductSelection = (land, product, selection = land) => {
  if (!product) {
    return land
  }

  land.remoteSensingSelections = {
    ...(land.remoteSensingSelections || {}),
    [product]: snapshotSelection(selection),
  }
  return land
}

const normalizeStoredSelections = (selections) =>
  Object.fromEntries(
    Object.entries(selections || {}).map(([product, selection]) => [
      product,
      snapshotSelection(selection),
    ])
  )

export class ViewImageState {
  constructor() {
    this.lands = {};
    this.landTiff = {}
    this.lastLandTiffKey = {}
    this.landAltimetry = {}
    this.landYieldMaps = {}
    this.landImageLayers = {}
  }

  updateImageType(landId, imageType) {
    let updatedLand = null
    this._updateLand(landId, (n) => {
      const previousProduct = getRemoteSensingProduct(n.imageType)
      const nextProduct = getRemoteSensingProduct(imageType)

      if (previousProduct && previousProduct !== nextProduct) {
        storeProductSelection(n, previousProduct)
      }

      n["imageType"] = imageType;

      if (nextProduct && previousProduct !== nextProduct) {
        const rememberedSelection = n.remoteSensingSelections?.[nextProduct]
        applySelection(n, rememberedSelection || {})
      }

      updatedLand = n
      return n
    });
    return updatedLand
  }

  updateImageDate(landId, date) {
    const selectedDate = normalizeSelectedDate(date)

    this._updateLand(landId, (n) => {
      n["selectedDate"] = selectedDate;
      delete n["selectedAcquisitionId"];
      delete n["selectedAcquisitionStatus"];
      delete n["selectedAccessId"];
      delete n["selectedCompanyPeriodId"];
      delete n["selectedItemId"];
      delete n["selectedQualityWarning"];
      delete n["remoteSensingSource"];
      // A freshly entered lot is rendered as Sentinel Visible before the
      // persisted state has an explicit imageType. Its first calendar choice
      // still belongs to Sentinel and must be remembered as such.
      const selectionProduct = getRemoteSensingProduct(n.imageType)
      if (selectionProduct || n.imageType == null) {
        storeProductSelection(
          n,
          selectionProduct || REMOTE_SENSING_PRODUCTS.SENTINEL
        )
      }
      return n
    });

    return selectedDate
  }

  updateRemoteSensingSelection(landId, selection = {}) {
    const selectedDate = normalizeSelectedDate(selection.date)

    this._updateLand(landId, (land) => {
      const next = {
        ...land,
        selectedDate,
        selectedAcquisitionId: selection.acquisitionId || null,
        selectedAcquisitionStatus: selection.status || null,
        selectedAccessId: selection.accessId || null,
        selectedCompanyPeriodId: selection.companyPeriodId || null,
        selectedItemId: selection.itemId || null,
        selectedQualityWarning: selection.qualityWarning || null,
        remoteSensingSource: selection.source || null,
      }
      const selectionProduct = normalizeRemoteSensingSource(
        selection.source || next.imageType || 'rgb'
      ).product
      storeProductSelection(next, selectionProduct)
      return next
    })

    return selectedDate
  }

  updateYieldMapId(landId, yieldMapId) {
    this._updateLand(landId, (n) => {
      n["selectedYieldMapId"] = yieldMapId;
      return n
    });
  }

  invalidateLandGeometry(landId) {
    const current = this.getLand(landId) || {}
    const next = {
      ...current,
      selectedDate: null,
      selectedYieldMapId: null,
    }

    delete next.selectedAcquisitionId
    delete next.selectedAcquisitionStatus
    delete next.selectedAccessId
    delete next.selectedCompanyPeriodId
    delete next.selectedItemId
    delete next.selectedQualityWarning
    delete next.remoteSensingSource
    delete next.remoteSensingSelections

    this.lands[landId] = next
    this._storeLand(landId, next)
    delete this.landTiff[landId]
    delete this.lastLandTiffKey[landId]
    delete this.landAltimetry[landId]
    delete this.landYieldMaps[landId]
    delete this.landImageLayers[landId]

    return next
  }

  invalidatePlanetHdAccess(landId) {
    const current = this.getLand(landId) || {}
    if (isHdImageType(current.imageType)) {
      this.updateImageDate(landId, null)
    }
    const currentAfterReset = this.getLand(landId) || {}
    if (currentAfterReset.remoteSensingSelections) {
      const nextSelections = {...currentAfterReset.remoteSensingSelections}
      delete nextSelections[REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD]
      currentAfterReset.remoteSensingSelections = nextSelections
      this.lands[landId] = currentAfterReset
      this._storeLand(landId, currentAfterReset)
    }
    // Product is the fourth encoded part in the shared layer cache key.
    const retainOtherProducts = cache => Object.fromEntries(
      Object.entries(cache || {}).filter(([key]) => key.split(':')[3] !== 'planetscope_hd')
    )
    this.landImageLayers[landId] = retainOtherProducts(this.landImageLayers[landId])
    this.landTiff[landId] = retainOtherProducts(this.landTiff[landId])
    if (!this.landTiff[landId][this.lastLandTiffKey[landId]]) {
      delete this.lastLandTiffKey[landId]
    }
    return this.getLand(landId)
  }

  getLand(landId) {
    if (this.lands[landId]) {
      return this.lands[landId];
    }

    const storedLand = this._readStoredLand(landId);
    if (storedLand) {
      this.lands[landId] = storedLand;
    }

    return storedLand;
  }

  getRemoteSensingSelection(landId, product) {
    if (!product) {
      return null
    }

    const land = this.getLand(landId)
    if (!land) {
      return null
    }

    if (getRemoteSensingProduct(land.imageType) === product) {
      return snapshotSelection(land)
    }

    const rememberedSelection = land.remoteSensingSelections?.[product]
    return rememberedSelection ? snapshotSelection(rememberedSelection) : null
  }

  _updateLand(landId, f) {
    if (!this.getLand(landId)) {
      this.lands[landId] = {
        imageType: null,
        selectedDate: null,
        selectedYieldMapId: null
      };
    }

    this.lands[landId] = f(this.lands[landId]);
    this._storeLand(landId, this.lands[landId]);
  }

  _readStoredLand(landId) {
    if (!landId || typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }

    try {
      const storedValue = JSON.parse(
        window.sessionStorage.getItem(`${VIEW_IMAGE_STORAGE_PREFIX}${landId}`)
      );
      const value = readDocumentScopedValue(storedValue)

      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null
      }

      const normalized = {
        ...value,
        selectedDate: normalizeSelectedDate(value.selectedDate),
      };
      if (value.remoteSensingSelections) {
        normalized.remoteSensingSelections = normalizeStoredSelections(value.remoteSensingSelections)
      }
      return normalized
    } catch (error) {
      return null;
    }
  }

  _storeLand(landId, value) {
    if (!landId || typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    try {
      window.sessionStorage.setItem(
        `${VIEW_IMAGE_STORAGE_PREFIX}${landId}`,
        JSON.stringify(createDocumentScopedValue(value))
      );
    } catch (error) {
      // The in-memory state remains authoritative when browser storage is unavailable.
    }
  }

  getImageLayerKey({
    accessId = '',
    companyPeriodId = '',
    algorithmVersion = '',
    date,
    enhanced = false,
    geometryHash = '',
    imageType,
    quality = 'standard',
    sceneId = '',
    source,
  }) {
    return buildRemoteSensingCacheKey({
      accessId,
      companyPeriodId,
      algorithmVersion,
      date,
      enhanced,
      geometryHash,
      landId: '',
      sceneId,
      source: source || {imageType, quality},
    });
  }

  addLandTiff(landId, image, options = {}) {
    if (!this.landTiff[landId]) {
      this.landTiff[landId] = {};
    }

    const cacheKey = this.getImageLayerKey(options);
    this.landTiff[landId][cacheKey] = image;
    this.lastLandTiffKey[landId] = cacheKey;
  }

  getLandTiff(landId, options = null) {
    const cache = this.landTiff[landId];
    if (!cache) {
      return null;
    }

    const cacheKey = options
      ? this.getImageLayerKey(options)
      : this.lastLandTiffKey[landId];

    return cache[cacheKey] || null;
  }

  addLandImageLayer(landId, options, payload) {
    if (!this.landImageLayers[landId]) {
      this.landImageLayers[landId] = {};
    }

    this.landImageLayers[landId][this.getImageLayerKey(options)] = payload;
  }

  getLandImageLayer(landId, options) {
    return this.landImageLayers[landId]?.[this.getImageLayerKey(options)] || null;
  }

  addLandAltimetry(landId, raster, geometryKey = null) {
    this.landAltimetry[landId] = { raster, geometryKey };
  }

  deleteLandAltimetry(landId) {
    delete this.landAltimetry[landId];
  }

  getLandAltimetry(landId, geometryKey = null) {
    const cached = this.landAltimetry[landId];

    if (!cached) {
      return null;
    }

    if (cached.raster == null) {
      return cached;
    }

    if (geometryKey != null && cached.geometryKey !== geometryKey) {
      return null;
    }

    return cached.raster;
  }

  addLandYieldMap(landId, yieldMapId, raster) {
    this.landYieldMaps[landId] = { yieldMapId, raster };
  }

  deleteLandYieldMap(landId) {
    delete this.landYieldMaps[landId];
  }

  getLandYieldMap(landId, yieldMapId = null) {
    const cached = this.landYieldMaps[landId];

    if (!cached) {
      return null;
    }

    if (yieldMapId != null && cached.yieldMapId !== yieldMapId) {
      return null;
    }

    return cached.raster;
  }
}

export const viewImageState = new ViewImageState();
